// frontend/src/pages/ProcessMapsList.js
import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from '../services/axios';
import toast from 'react-hot-toast';
import { ArrowLeftIcon, PlusIcon } from '@heroicons/react/24/outline';
import ProcessFlowVisualizer from '../components/process/ProcessFlowVisualizer';

const ProcessMapsList = () => {
    const { businessId } = useParams();
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [business, setBusiness] = useState(null);
    const [processMaps, setProcessMaps] = useState([]);
    const [activeMapId, setActiveMapId] = useState(null);

    useEffect(() => {
        loadData();
    }, [businessId]);

    const loadData = async () => {
        try {
            setLoading(true);
            const busRes = await axios.get(`/business/${businessId}`);
            setBusiness(busRes.data.data);

            const assessment = busRes.data.data.assessment;
            if (assessment) {
                const mapsRes = await axios.get(`/process-maps/assessment/${assessment.id}`);
                setProcessMaps(mapsRes.data.data || []);
                if (mapsRes.data.data?.length > 0) {
                    setActiveMapId(mapsRes.data.data[0].id);
                }
            }
        } catch (error) {
            toast.error('Failed to load process maps');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="text-gray-600">Loading process maps...</div>
            </div>
        );
    }

    const activeMap = processMaps.find(m => m.id === activeMapId) || processMaps[0];

    return (
        <div className="w-full space-y-6">
            <Link to={`/business/${businessId}`} className="inline-flex items-center text-sm text-gray-600 hover:text-black space-x-1">
                <ArrowLeftIcon className="w-4 h-4" />
                <span>Back to Business Details</span>
            </Link>

            <div className="bg-white rounded-lg border border-gray-200 p-6 shadow-sm flex justify-between items-center flex-wrap gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Process Maps</h1>
                    <p className="text-sm text-gray-600 mt-1">
                        Map and optimize operations for {business?.name || 'Business'}.
                    </p>
                </div>
                <Link
                    to="/process-mapping"
                    className="px-4 py-2 bg-black text-white text-sm rounded-md hover:bg-gray-800 flex items-center space-x-1 font-medium"
                >
                    <PlusIcon className="w-4 h-4" />
                    <span>New Process Map</span>
                </Link>
            </div>

            {processMaps.length === 0 ? (
                <div className="bg-white rounded-lg border border-gray-200 p-12 text-center space-y-3">
                    <p className="text-gray-500">No process maps recorded for this business assessment yet.</p>
                    <Link
                        to="/process-mapping"
                        className="inline-block px-4 py-2 bg-black text-white text-sm font-medium rounded-md hover:bg-gray-800"
                    >
                        Create Process Map
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div className="space-y-2 md:col-span-1">
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider px-1">Maps</h3>
                        {processMaps.map((map) => (
                            <button
                                key={map.id}
                                onClick={() => setActiveMapId(map.id)}
                                className={`w-full text-left p-3 rounded-md border text-sm transition-all ${
                                    activeMapId === map.id 
                                        ? 'bg-black text-white border-black shadow' 
                                        : 'bg-white text-gray-800 border-gray-200 hover:bg-gray-50'
                                }`}
                            >
                                <div className="font-medium truncate">{map.name}</div>
                                <div className="flex justify-between items-center mt-1 text-xs opacity-75 capitalize">
                                    <span>{map.status}</span>
                                    <span>{map.steps?.length || 0} steps</span>
                                </div>
                            </button>
                        ))}
                    </div>

                    <div className="md:col-span-3 bg-white rounded-lg border border-gray-200 p-6 space-y-6">
                        {activeMap && (
                            <>
                                <div className="flex justify-between items-start border-b border-gray-100 pb-4">
                                    <div>
                                        <div className="flex items-center space-x-2">
                                            <h2 className="text-xl font-bold text-gray-900">{activeMap.name}</h2>
                                            <span className="px-2 py-0.5 text-xs rounded bg-gray-100 font-medium capitalize">
                                                {activeMap.status}
                                            </span>
                                        </div>
                                        <p className="text-sm text-gray-600 mt-1">{activeMap.description}</p>
                                    </div>
                                    <Link
                                        to={`/process-mapping/${activeMap.id}`}
                                        className="px-3 py-1.5 border border-gray-300 rounded text-xs font-medium hover:bg-gray-50"
                                    >
                                        Edit Map
                                    </Link>
                                </div>

                                <ProcessFlowVisualizer 
                                    steps={activeMap.steps || []}
                                    bottlenecks={activeMap.bottlenecks || []}
                                    aiOpportunities={activeMap.ai_opportunities || []}
                                />
                            </>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ProcessMapsList;
